import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
  Group,
  Input,
  InputBase,
  Loader,
  ScrollArea,
  Stack,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";

import {
  ConsultationComboboxItem,
  ConsultationServiceComboboxItem,
} from "@customTypes/index";

import { StyledInputBase } from "./ConsultationSelect.styled";


function SelectOption({ label, date }: ConsultationComboboxItem) {
  return (
    <Stack gap={5}>
      <Text fz={14} ff={"var(--font-secondary)"} fw={450}>
        {label}
      </Text>
      <Text fz={12} c="dimmed">
        {date}
      </Text>
    </Stack>
  );
}

export interface ConsultationSelectProps<
  V = string | null,
  T = ConsultationComboboxItem
> {
  placeholder?: string;
  label?: string;
  value: V;
  setValue: React.Dispatch<React.SetStateAction<V>>;
  data: T[] | [];
  disabled?: boolean;
  isLoading?: boolean;
  withAsterisk?: boolean;
  required?: boolean;
}

export const ConsultationSelect = ({
  placeholder,
  label,
  value,
  setValue,
  data,
  disabled,
  isLoading = false,
  withAsterisk = false,
  required = false,
}: ConsultationSelectProps) => {
  const { t } = useTranslation();
  //   const [value, setValue] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  const selectedOption = data?.find(item => item.value === value);

  const options = data
    ?.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase().trim())
    )
    .map(item => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        active={item.value === value}>
        <Group gap="xs" justify="space-between">
          <SelectOption {...item} />
          {item.value === value && (
            <IconCheck size={16} color={"var(--mantine-color-black)"} />
            //   <CheckIcon size={12} color={"var(--mantine-color-black)"} />
          )}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={val => {
        // console.log(val);
        setValue(val);
        combobox.closeDropdown();
      }}>
      <Combobox.Target>
        <StyledInputBase>
          <InputBase
            component="button"
            withAsterisk={withAsterisk}
            required={required}
            type="button"
            disabled={disabled}
            label={label}
            pointer
            rightSection={
              isLoading ? (
                <Loader size={16} color="accent.4" />
              ) : (
                <Combobox.Chevron />
              )
            }
            onClick={() => combobox.toggleDropdown()}
            rightSectionPointerEvents="none"
            multiline>
            {selectedOption ? (
              <SelectOption {...selectedOption} />
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </InputBase>
        </StyledInputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder={t("search-for-consultation")}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={200} type="auto" scrollbarSize={7}>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>{t("nothing-found")}</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
